import { useTranslation } from 'react-i18next';
import { UserCog } from 'lucide-react';

import { useMedicalStaffListStore } from '../store/medical-staff-list.store';
import type {
  MedicalStaff,
  MedicalStaffSortKey,
} from '../types/medical-staff.types';
import { MedicalStaffListRow } from './MedicalStaffListRow';

interface MedicalStaffListTableProps {
  staff: readonly MedicalStaff[];
}

interface ColumnDef {
  // 'phone' / 'status' / 'manage' are data/action columns, not sortable per PRD 03 §4.1.
  key: 'phone' | MedicalStaffSortKey | 'status' | 'manage';
  i18nKey: string;
  sortable: boolean;
}

// 8 data columns per PRD 03 §2.2 #7 / §4.1, plus a 9th management column (prototype
// parity) for row actions: Edit / Resend / Withdraw — see MedicalStaffListRow.
// Natural auto-layout (prototype institution-managers.html has no per-column widths).
// The variable-length text columns (name/email/department/jobRole) are capped and
// truncated with an ellipsis + tooltip in MedicalStaffListRow so long values don't
// wrap or stretch the table (SHCT-255).
const COLUMNS: readonly ColumnDef[] = [
  { key: 'id', i18nKey: 'id', sortable: true },
  { key: 'name', i18nKey: 'name', sortable: true },
  { key: 'email', i18nKey: 'email', sortable: true },
  { key: 'phone', i18nKey: 'phone', sortable: false },
  { key: 'department', i18nKey: 'department', sortable: true },
  { key: 'jobRole', i18nKey: 'jobRole', sortable: true },
  { key: 'registeredAt', i18nKey: 'registeredAt', sortable: true },
  { key: 'status', i18nKey: 'status', sortable: false },
  { key: 'manage', i18nKey: 'manage', sortable: false },
];

function renderSortIcon(
  col: ColumnDef,
  sortKey: MedicalStaffSortKey,
  sortDir: 'asc' | 'desc',
): string {
  if (!col.sortable) return '';
  if (sortKey !== col.key) return '↕';
  return sortDir === 'asc' ? '↑' : '↓';
}

function ColumnHeaders() {
  const { t } = useTranslation('admin');
  const sort = useMedicalStaffListStore((s) => s.sort);
  const toggleSort = useMedicalStaffListStore((s) => s.toggleSort);

  return (
    <tr>
      {COLUMNS.map((col) => (
        <th
          key={col.key}
          style={{
            whiteSpace: 'nowrap',
            cursor: col.sortable ? 'pointer' : undefined,
          }}
          onClick={
            col.sortable
              ? () => toggleSort(col.key as MedicalStaffSortKey)
              : undefined
          }
        >
          {t(`medicalStaffManagement.list.columns.${col.i18nKey}`)}{' '}
          {col.sortable && (
            <span className="sort-icon">{renderSortIcon(col, sort.key, sort.dir)}</span>
          )}
        </th>
      ))}
    </tr>
  );
}

export function MedicalStaffListTable({ staff }: MedicalStaffListTableProps) {
  return (
    <div className="card">
      <table className="table table--clickable" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {staff.map((row) => (
            <MedicalStaffListRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Skeleton = 5 rows × 8 cols.
export function MedicalStaffListSkeleton() {
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {COLUMNS.map((col) => (
                <td key={col.key}>
                  <div
                    className="animate-pulse"
                    style={{ height: 14, background: 'var(--muted)', borderRadius: 4 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// PRD 03 §3.1 / §3.2: empty copy differs when search/department filter is active.
// Icon mirrors the prototype's empty state illustration (`user-cog`).
export function MedicalStaffListEmpty({ filtered }: { filtered: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={COLUMNS.length}
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'var(--muted-foreground)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <UserCog size={48} color="var(--muted-foreground)" />
                <p style={{ margin: 0 }}>
                  {filtered
                    ? t('medicalStaffManagement.list.emptySearch')
                    : t('medicalStaffManagement.list.empty')}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
